/**
 * Tractor Metrics (SW-8879): production readiness over a span, read only.
 *
 * The view is the URL — span, end day, customer and allocation filters — and
 * one call, `GET /v1/activity/fleet`, supplies everything on the page: the
 * fleet right now, each customer against its threshold, and every tractor
 * with one two-lane cell per day (readiness from Tractor State over activity
 * from the data lake). Nothing is written from here: a readiness change is
 * made in Tractor State with an "as of" time, and this page re-reads it.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { styled } from "styled-components";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import PageHeader from "../../components/PageHeader/PageHeader";
import { FullPageContainer, Right } from "../Dashboard/Dashboard";
import { Button } from "../../components/ui/button";
import { Container, Page } from "../TractorState/styled";
import { tokens } from "../TractorState/tokens";
import { fetchFleet } from "./api";
import CustomerCards from "./CustomerCards";
import DayView from "./DayView";
import FleetFilters from "./FleetFilters";
import FleetTable from "./FleetTable";
import RightNow from "./RightNow";
import SpanControl from "./SpanControl";
import { FleetQuery, parseQuery, spanBounds, toSearch } from "./query";
import { todayIso } from "./time";
import { FleetResponse } from "./types";

type State =
  | { status: "loading"; fleet: FleetResponse | null }
  | { status: "ok"; fleet: FleetResponse }
  | { status: "error"; message: string; fleet: FleetResponse | null };

interface TractorMetricsProps {
  token?: string;
}

const TractorMetrics: React.FC<TractorMetricsProps> = ({ token }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(
    () => parseQuery(searchParams.toString()),
    [searchParams],
  );
  const bounds = spanBounds(query);
  const [state, setState] = useState<State>({ status: "loading", fleet: null });
  const [reloads, setReloads] = useState(0);

  const update = useCallback(
    (next: FleetQuery) => setSearchParams(toSearch(next)),
    [setSearchParams],
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setState((s) => ({ status: "loading", fleet: s.fleet }));
    fetchFleet(token, {
      from: bounds.from,
      to: bounds.to,
      customer: query.customer,
      allocation: query.allocation,
      rigs: query.rigs,
    }).then(
      (fleet) => !cancelled && setState({ status: "ok", fleet }),
      (err) =>
        !cancelled &&
        setState((s) => ({
          status: "error",
          message:
            err instanceof Error ? err.message : "The fleet did not load.",
          fleet: s.fleet,
        })),
    );
    return () => {
      cancelled = true;
    };
  }, [
    token,
    bounds.from,
    bounds.to,
    query.customer,
    query.allocation,
    query.rigs,
    reloads,
  ]);

  const fleet = state.fleet;

  return (
    <Shell>
      <Navbar />
      <Right>
        <PageHeader>Tractor Metrics</PageHeader>
        <Page>
          <Container $maxWidth={1280}>
            <Header>
              <div>
                <Title>Tractor Metrics</Title>
                <Sub>
                  Fleet health over a span. Readiness comes from Tractor State;
                  activity comes from the data lake. This page only reads.
                </Sub>
              </div>
              <SpanControl query={query} onChange={update} />
            </Header>

            <FleetFilters query={query} fleet={fleet} onChange={update} />

            {state.status === "error" && (
              <Notice role="alert">
                The fleet did not load: {state.message}{" "}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReloads((n) => n + 1)}
                >
                  Retry
                </Button>
              </Notice>
            )}
            {state.status === "loading" && !fleet && (
              <Notice>Loading the fleet…</Notice>
            )}

            {fleet && (
              <Stack $busy={state.status === "loading"}>
                <RightNow fleet={fleet} />
                <CustomerCards customers={fleet.customers} />
                {query.span === "day" ? (
                  <>
                    <BackRow>
                      <BackButton
                        type="button"
                        onClick={() =>
                          update({ ...query, span: "7d", from: undefined })
                        }
                      >
                        ← Back to the 7-day span
                      </BackButton>
                      <Sub as="span">
                        One day, hour by hour, in each tractor&apos;s own zone.
                      </Sub>
                    </BackRow>
                    <DayView
                      token={token}
                      day={query.to}
                      fleet={fleet}
                      nowIso={
                        query.to === todayIso()
                          ? new Date().toISOString()
                          : null
                      }
                    />
                  </>
                ) : (
                  <FleetTable fleet={fleet} query={query} />
                )}
                <Sub>
                  Within a cell, left is 00:00 and right is 24:00, so a busy
                  midday reads as a blue block in the middle. Click a cell to
                  open that day. Nothing is written from this page: a readiness
                  change is made in Tractor State with an &quot;as of&quot;
                  time, and this page re-reads it.
                </Sub>
              </Stack>
            )}
          </Container>
        </Page>
        <Footer />
      </Right>
    </Shell>
  );
};

export default TractorMetrics;

const Shell = styled(FullPageContainer)`
  @media (min-width: 1033px) {
    height: 100vh;
    min-height: 0;
    overflow: hidden;
  }
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 24px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  margin: 0;
  font: 700 22px ${tokens.fontSans};
  color: ${tokens.navy};
`;

const Sub = styled.p`
  margin: 4px 0 0;
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const Notice = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
  font: 13px ${tokens.fontSans};
  color: ${tokens.ink};
`;

const BackRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
`;

const BackButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.blue};
  cursor: pointer;
`;

const Stack = styled.div<{ $busy: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  padding-bottom: 24px;
  opacity: ${(p) => (p.$busy ? 0.6 : 1)};
  transition: opacity 120ms;
`;
